import { Box, Stack } from '@mui/material';
import { getPortfolios } from 'lib/api/integrations/digitalSignature';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { Portfolio } from 'lib/types/models';
import { PortfolioStatus } from 'lib/types/models';
import { Pagination } from 'lib/types/shared';
import PortfolioModal from 'pages/BusinessProfile/Contracts/Portfolios/Components/PortfolioModal';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

const STATUS_OPTIONS = Object.entries(PortfolioStatus).map(
  ([value, label]) => ({
    label,
    value,
  }),
);

const tableHeaders = [
  { label: 'Título', key: 'title', type: 'text' },
  { label: 'Estado', key: 'status', type: 'status-chip' },
  { label: 'Empresa', key: 'business', type: 'dual-text' },
  { label: 'Operación', key: 'requestType', type: 'text' },
  { label: 'Fecha de Creación', key: 'createdAt', type: 'date' },
  { label: 'Fecha de Actualización', key: 'updatedAt', type: 'date' },
  { label: 'Validaciones', key: 'validationsText', type: 'text' },
  { label: 'Válido hasta', key: 'validUntil', type: 'date' },
];

const Portfolios = () => {
  const dispatch = useDispatch();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);
  const [status, setStatus] = useState<{ value: string; label: string }[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [identifier, setIdentifier] = useState<string | null>(null);

  const buildQuery = (params?: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    const query = new URLSearchParams();
    const { page = pagination.page } = params ?? {};
    const { pageSize = pagination.pageSize } = params ?? {};
    const { exportComplete } = params ?? { exportComplete: false };
    query.append('page', page.toString());
    query.append('pageSize', pageSize.toString());
    if (exportComplete) query.append('exportComplete', 'true');
    if (status.length > 0) {
      query.append('status', status.map((s) => String(s.value)).join(','));
    }
    if (startDate && endDate) {
      query.append('startDate', startDate);
      query.append('endDate', endDate);
    }
    if (identifier) query.append('businessIdentifier', identifier);
    return query.toString();
  };

  const fetchPortfolios = async (params?: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    try {
      setLoading(true);
      const query = buildQuery(params);
      const response = await getPortfolios(dispatch, query);
      if (!params?.exportComplete) {
        setPortfolios(response.data);
        setPagination(response.pagination);
      }
      return response;
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickRow = (id: string) => {
    setOpenDialog(true);
    setPortfolio(portfolios.find((p) => p.id === id) || null);
  };

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha inicio',
      dateKey: 'startDate',
      value: startDate,
      setter: setStartDate,
    },
    {
      type: 'date',
      label: 'Fecha fin',
      dateKey: 'endDate',
      value: endDate,
      setter: setEndDate,
    },
    {
      type: 'multiple',
      label: 'Estado',
      value: status,
      setter: setStatus,
      options: STATUS_OPTIONS,
    },
    {
      type: 'autocomplete',
      label: 'Empresa',
      key: 'walletBusiness',
      value: identifier,
      setter: setIdentifier,
    },
  ];

  useEffect(() => {
    void fetchPortfolios();
  }, []);

  return (
    <>
      <Stack direction="column" gap={2} mt={2} position="relative">
        <Box sx={{ position: 'absolute', top: -56, right: 0 }}>
          <GlobalSearcher
            filters={filters}
            triggerSearch={() => fetchPortfolios()}
            dispatch={dispatch}
          />
        </Box>
        <TableWithPagination
          data={portfolios}
          setData={setPortfolios}
          dataHeaders={tableHeaders}
          showActions
          pagination={pagination}
          setPagination={setPagination}
          searchData={fetchPortfolios}
          setLoading={setLoading}
          exportToFile="xlsx"
          loading={loading}
          exportComplete
          lastElement
          handleClickRow={handleClickRow}
        />
      </Stack>
      <PortfolioModal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        portfolio={portfolio}
        onPortfolioUpdated={() =>
          void fetchPortfolios({ page: 1, pageSize: 10 })
        }
      />
    </>
  );
};

export default Portfolios;
